import { IDefectResponse } from "./interfaces";

export function normalizeDefectRow(defect: unknown): {
  idPoint: number | undefined;
  idTrainComponent: number | undefined;
  idTrainMechanism: number | undefined;
  resolvedIdTrainMechanism: number | undefined;
  rate: number;
} {
  const r = defect as Record<string, unknown>;
  const num = (v: unknown): number | undefined => {
    if (v === null || v === undefined || v === "") return undefined;
    const x = Number(v);
    return Number.isNaN(x) ? undefined : x;
  };

  return {
    idPoint: num(r.idPoint ?? r.idpoint),
    idTrainComponent: num(r.idTrainComponent ?? r.idtraincomponent),
    idTrainMechanism: num(r.idTrainMechanism ?? r.idtrainmechanism),
    resolvedIdTrainMechanism: num(r.resolvedIdTrainMechanism ?? r.resolvedidtrainmechanism),
    rate: (() => {
      const v = r.Rate ?? r.rate;
      if (v === null || v === undefined || v === "") return 0;
      const x = Number(v);
      return Number.isNaN(x) ? 0 : x;
    })(),
  };
}

function getResolvedMechanismId(n: ReturnType<typeof normalizeDefectRow>): number | null {
  const resolved = n.resolvedIdTrainMechanism;
  if (resolved != null && !Number.isNaN(Number(resolved))) {
    return Number(resolved);
  }
  const tm = n.idTrainMechanism;
  return tm != null && !Number.isNaN(Number(tm)) ? Number(tm) : null;
}

function buildPointToComponentId(defects: IDefectResponse[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const d of defects) {
    const n = normalizeDefectRow(d);
    if (n.idPoint != null && n.idTrainComponent != null) {
      m.set(Number(n.idPoint), Number(n.idTrainComponent));
    }
  }
  return m;
}

function buildPointToMechanismId(defects: IDefectResponse[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const d of defects) {
    const n = normalizeDefectRow(d);
    if (n.idPoint == null) continue;
    const mech = getResolvedMechanismId(n);
    if (mech != null) {
      m.set(Number(n.idPoint), mech);
    }
  }
  return m;
}

function buildComponentToMechanismId(defects: IDefectResponse[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const d of defects) {
    const n = normalizeDefectRow(d);
    if (n.idTrainComponent == null) continue;
    const mech = getResolvedMechanismId(n);
    if (mech != null) {
      m.set(Number(n.idTrainComponent), mech);
    }
  }
  return m;
}

export function filterDefectsByBinding(
  defects: IDefectResponse[],
  binding: { bindObjectType: number; idBindObject: number | null } | null | undefined,
): IDefectResponse[] {
  if (!binding) {
    return defects;
  }

  const { bindObjectType, idBindObject } = binding;

  if (bindObjectType === 2) {
    return defects;
  }

  if (idBindObject == null) {
    return defects;
  }

  const pointToComponent = buildPointToComponentId(defects);
  const pointToMechanism = buildPointToMechanismId(defects);
  const componentToMechanism = buildComponentToMechanismId(defects);
  const bind = Number(idBindObject);

  switch (bindObjectType) {
    case 3:
      return defects.filter(d => {
        const n = normalizeDefectRow(d);
        return n.idPoint != null && Number(n.idPoint) === bind;
      });
    case 16:
      return defects.filter(d => {
        const n = normalizeDefectRow(d);
        const mech = getResolvedMechanismId(n);
        if (mech === bind) {
          return true;
        }
        if (n.idPoint != null && pointToMechanism.get(Number(n.idPoint)) === bind) {
          return true;
        }
        if (n.idTrainComponent != null && componentToMechanism.get(Number(n.idTrainComponent)) === bind) {
          return true;
        }
        return false;
      });
    case 18:
      return defects.filter(d => {
        const n = normalizeDefectRow(d);
        if (n.idTrainComponent != null && Number(n.idTrainComponent) === bind) {
          return true;
        }
        if (n.idPoint != null && pointToComponent.get(Number(n.idPoint)) === bind) {
          return true;
        }
        return false;
      });
    default:
      return defects;
  }
}
